// ─── 🎓 Le lezioni libere, e i posti che si liberano ─────────────────────────
//
// 🗣️ 28/09/2026 — «Notifiche ai soci: testi e frequenza», mockup «Notifiche ai
// soci» approvato. Chi ha detto sì a «🎓 Lezioni» riceve dal bot le ore in cui
// un maestro è libero, e l'avviso quando una lezione viene disdetta e quell'ora
// torna libera. Il bot le legge dal ponte, **in sola lettura**: a prenotarle
// resta la strada di sempre.
//
// ⚖️ Libera vuol dire quello che vede il maestro nella sua app: un'ora dentro il
// suo perimetro (le ore che la Segreteria gli ha aperto, meno i giorni in cui ha
// detto che non c'è), su un campo libero, dentro l'orario del circolo, e senza
// che lui sia già su un altro campo (⇒ `spaziDelMaestro`). Un calcolo solo, lo
// stesso per il maestro e per il socio.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
/** Quanti giorni al massimo si chiedono in una volta. */
export const GIORNI_MASSIMI_LEZIONI = 14;
/**
 * Quante ore libere al massimo escono in una risposta.
 * 🔄 18/10/2026 (mockup «Lezioni dal bot»): era 40, e quattordici giorni di più
 * maestri li tagliava a metà. Il bot ci costruisce il calendario delle lezioni:
 * gli servono tutte.
 */
export const LEZIONI_MASSIME = 500;
/**
 * 🎓 18/10/2026 — **i tipi che il socio prenota dal bot**. 🗣️ *«le lezioni possono
 * essere singola doppia a tre persone e gli allenamenti solo a quattro persone»* ·
 * *«non serve che indica quante persone sono, perché a seconda della tipologia di
 * lezione, in automatico, si sa quante persone sono»*. I corsi dal bot no. Il
 * gestionale ne conosce di più (⇒ `tariffe-lezioni.ts`): quelli li fissa la Segreteria.
 */
export const TIPI_DI_LEZIONE_DAL_BOT = {
    singola: { kind: 'lezione', people: 1 },
    doppia: { kind: 'lezione', people: 2 },
    a_tre: { kind: 'lezione', people: 3 },
    // 🆕 23/10/2026 (mockup «Tutti i tipi di lezione», A: 🗣️ *«i tipi di lezione vanno bene»*): il listino ha la lezione
    // fino a quattro (⇒ `PERSONE_MASSIME_LEZIONE`), e dal bot mancava.
    a_quattro: { kind: 'lezione', people: 4 },
    allenamento: { kind: 'allenamento', people: 4 },
    // 🆕 20/10/2026 (mockup «Partita tattica nel bot», «1A 2 sì»): tre e il maestro, la quota del maestro.
    tattica: { kind: 'tattica', people: 3 },
};
export const tipoDiLezioneDalBotSchema = z.enum([
    'singola',
    'doppia',
    'a_tre',
    'a_quattro',
    'allenamento',
    'tattica',
]);
/** Il nome del posto che il socio prenota per un altro allievo: il nome vero lo segna la Segreteria. */
export const ALLIEVO_DA_SEGNARE = 'Allievo da segnare';
export const domandaLezioniLibereSchema = z.object({
    from: dataSchema,
    to: dataSchema,
    /** 🎓 18/10/2026: il tipo dice la durata e la quota a testa. Senza, la lezione singola. */
    type: tipoDiLezioneDalBotSchema.default('singola'),
    /** Facoltativo: quante righe al massimo. Senza, tutte (fino a `LEZIONI_MASSIME`). */
    limit: z.coerce.number().int().min(1).max(LEZIONI_MASSIME).optional(),
});
export const domandaLezioniLiberateSchema = z.object({ since: z.iso.datetime({ offset: true }) });
/** Un'ora libera di un maestro: chi, quando, quanto dura. */
export const oraDelMaestroSchema = z.object({
    coachId: z.uuid(),
    /** Il nome del maestro come lo leggono i soci nella partita («Lezione con …»). */
    coachName: z.string(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int(),
});
export const lezioneLiberaSchema = oraDelMaestroSchema.extend({
    /**
     * 🆕 18/10/2026: quanto paga il socio, dal «Listino prezzi lezioni» (lezione da
     * uno). Un'ora che il listino non sa quotare non esce: una casella vuota non è zero.
     */
    perPlayerCents: z.number().int().min(0),
});
export const lezioniLibereSchema = z.object({ items: z.array(lezioneLiberaSchema) });
export const lezioneLiberataSchema = oraDelMaestroSchema.extend({
    /** Quando la lezione è stata disdetta: l'istante in cui quell'ora è tornata libera. */
    freedAt: z.string(),
});
export const lezioniLiberateSchema = z.object({ items: z.array(lezioneLiberataSchema) });
/**
 * 🎓 18/10/2026 — **prenotare una lezione dal bot** (mockup «Lezioni dal bot»). Il
 * maestro, il giorno e l'ora di un'ora libera; il campo lo sceglie il gestionale
 * (il primo libero, nell'ordine dei campi). La durata è quella della lezione.
 * 🔑 `idempotencyKey` come `POST /bookings`: la stessa chiave è lo stesso gesto.
 */
export const prenotaLezioneDalPonteSchema = z.object({
    coachId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    /** 🎓 18/10/2026: senza, la lezione singola (com'era). */
    type: tipoDiLezioneDalBotSchema.default('singola'),
    idempotencyKey: z.string().trim().min(8).max(200),
});
//# sourceMappingURL=lezioni-libere.js.map